import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Send, CheckCircle, AlertTriangle, Loader2, RefreshCcw, Beaker, Rocket, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { api, friendlyError } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Welcome Email Backfill panel — UI for the /api/admin/welcome-backfill/* endpoints.
 *
 * The flow mirrors the safety controls of the backend:
 *  1. Load /preview to show counts (no sends).
 *  2. Send a single test to a chosen email (no DB write).
 *  3. Dry-run the mass operation (lists who would receive).
 *  4. Confirm + run real backfill.
 */
export default function EmailBackfillPanel() {
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [testTo, setTestTo] = useState('');
  const [testRole, setTestRole] = useState('user');
  const [testSending, setTestSending] = useState(false);

  const [dryRun, setDryRun] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const loadAll = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const [c, p] = await Promise.all([api.adminEmailConfig(), api.adminBackfillPreview()]);
      setConfig(c);
      setPreview(p);
    } catch (e) {
      toast({ title: 'No pudimos cargar el panel', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setLoadingPreview(false);
    }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onTest = async () => {
    if (!testTo) {
      return;
    }
    setTestSending(true);
    try {
      const res = await api.adminBackfillTest(testTo, testRole);
      toast({
        title: res.sent ? 'Test enviado' : 'No se envió',
        description: res.sent
          ? `Welcome (${testRole}) enviado a ${testTo}. Revisa tu inbox.`
          : 'Resend rechazó el envío. Revisa la configuración.',
      });
    } catch (e) {
      toast({ title: 'Error en el test', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setTestSending(false);
    }
  };

  const onDryRun = async () => {
    setDryRunning(true);
    setDryRun(null);
    try {
      const res = await api.adminBackfillRun(true, 500);
      setDryRun(res);
    } catch (e) {
      toast({ title: 'Error en dry-run', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setDryRunning(false);
    }
  };

  const onRun = async () => {
    const n = preview?.will_be_included_in_backfill ?? 0;
    const ok = window.confirm(
      `¿Confirmas enviar el welcome real a ${n} usuario(s)?\n\n` +
      `Solo se marcará welcome_email_sent=true en los envíos exitosos.`
    );
    if (!ok) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await api.adminBackfillRun(false, 500);
      setResult({ ...res, at: new Date().toISOString() });
      toast({
        title: `Backfill completado`,
        description: `Enviados ${res.sent_ok} · Fallidos ${res.failed_count} · Omitidos ${res.skipped}`,
      });
      await loadAll();
    } catch (e) {
      toast({ title: 'Error en el backfill', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  if (loadingPreview && !preview) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
        <Loader2 className="w-8 h-8 text-habita animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-3">Cargando configuración de emails...</p>
      </div>
    );
  }

  const cfgOk = config?.resend?.configured;

  return (
    <div className="space-y-6" data-testid="email-backfill-panel">
      {/* Resend config status */}
      <div className={`rounded-2xl border p-5 ${cfgOk ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          {cfgOk
            ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-habita-dark text-sm">
              Resend: {cfgOk ? 'configurado y listo' : 'no configurado (modo simulado)'}
            </p>
            <p className="text-xs text-gray-600 mt-0.5 truncate">
              FROM: {config?.from_email} · REPLY-TO: {config?.reply_to_email} · {config?.resend?.mode}
            </p>
          </div>
          <button onClick={loadAll} className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-300 hover:bg-white flex items-center gap-1" data-testid="email-backfill-refresh">
            <RefreshCcw className="w-3 h-3" /> Refrescar
          </button>
        </div>
      </div>

      {/* Step 1: counts */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
          <Info className="w-4 h-4 text-habita" /> Estado del backfill
        </h3>
        {preview ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Usuarios totales', value: preview.total_users },
                { label: 'Ya recibieron', value: preview.already_received_welcome, accent: 'text-emerald-600' },
                { label: 'Pendientes', value: preview.never_received_welcome, accent: 'text-amber-600' },
                { label: 'Incluidos en backfill', value: preview.will_be_included_in_backfill, accent: 'text-habita' },
              ].map(s => (
                <div key={s.label} className="bg-habita-cream rounded-xl p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${s.accent || 'text-habita-dark'}`} data-testid={`backfill-stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>{s.value}</p>
                </div>
              ))}
            </div>
            {preview.sample_first_5?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Primeros 5 candidatos</p>
                <div className="text-xs text-gray-600 space-y-1">
                  {preview.sample_first_5.map(s => (
                    <div key={s.email} className="flex flex-wrap items-center gap-2 font-mono">
                      <span className="text-gray-400">·</span>
                      <span>{s.email}</span>
                      <span className="text-gray-400">→</span>
                      <span>{s.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-400">[{s.role}]</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 mt-3">No hay datos disponibles.</p>
        )}
      </div>

      {/* Step 2: test send */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
          <Beaker className="w-4 h-4 text-habita" /> Paso 1 — Test a un email
        </h3>
        <p className="text-sm text-gray-600 mt-1">Envía un único welcome a un email para validar deliverability. No marca DB.</p>
        <div className="grid sm:grid-cols-[1fr,160px,auto] gap-2 mt-4">
          <Input
            type="email"
            placeholder="tu_email@gmail.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            data-testid="email-backfill-test-input"
            className="h-11"
          />
          <select
            value={testRole}
            onChange={(e) => setTestRole(e.target.value)}
            data-testid="email-backfill-test-role"
            className="h-11 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="user">user (compradores)</option>
            <option value="agent">agent / inmobiliaria</option>
          </select>
          <Button
            onClick={onTest}
            disabled={!testTo || testSending || !cfgOk}
            data-testid="email-backfill-test-btn"
            className="btn-habita h-11 px-5 rounded-full"
          >
            {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" /> Enviar test</>}
          </Button>
        </div>
        {!cfgOk && <p className="text-xs text-amber-700 mt-2">⚠️ Resend no está configurado — el botón está deshabilitado.</p>}
      </div>

      {/* Step 3: dry-run + run */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
          <Rocket className="w-4 h-4 text-habita" /> Paso 2 — Backfill masivo
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Envía el welcome a todos los usuarios pendientes (oldest first, 6 envíos/seg). Idempotente: solo marca DB en envíos exitosos.
        </p>

        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            onClick={onDryRun}
            disabled={dryRunning || !cfgOk}
            variant="outline"
            data-testid="email-backfill-dryrun-btn"
            className="h-11 px-5 rounded-full"
          >
            {dryRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Info className="w-4 h-4 mr-1.5" /> Dry-run (sin enviar)</>}
          </Button>
          <Button
            onClick={onRun}
            disabled={running || !cfgOk || !preview?.will_be_included_in_backfill}
            data-testid="email-backfill-run-btn"
            className="btn-habita h-11 px-5 rounded-full"
          >
            {running
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enviando...</>
              : <><Send className="w-4 h-4 mr-1.5" /> Ejecutar backfill real</>}
          </Button>
        </div>

        {dryRun && (
          <div className="mt-5 p-4 rounded-xl bg-habita-cream">
            <p className="text-sm font-semibold text-habita-dark mb-2">Dry-run · {dryRun.total_processed} candidatos</p>
            <p className="text-xs text-gray-600">Si ejecutas el backfill real ahora, se enviará el welcome a estos usuarios. Ningún envío se ha realizado.</p>
          </div>
        )}

        {result && (
          <div className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="email-backfill-result">
            <p className="text-sm font-semibold text-habita-dark mb-2 flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              Backfill ejecutado · {new Date(result.at).toLocaleString('es-CO')}
            </p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div><span className="text-gray-500">Enviados:</span> <strong className="text-emerald-700">{result.sent_ok}</strong></div>
              <div><span className="text-gray-500">Fallidos:</span> <strong className="text-rose-700">{result.failed_count}</strong></div>
              <div><span className="text-gray-500">Omitidos:</span> <strong className="text-gray-700">{result.skipped}</strong></div>
            </div>
            {result.failed?.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs font-semibold text-rose-700 cursor-pointer">Ver fallidos ({result.failed.length})</summary>
                <div className="mt-2 max-h-48 overflow-y-auto text-xs text-gray-700 space-y-1 font-mono">
                  {result.failed.map((f, i) => (
                    <div key={i}>· {f.email} — {f.error}</div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
